import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Download, Check, X, FileText, Loader2, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { GroupedLeadConversion, QuoteDetail } from '../../types';

interface ConversionsTableProps {
  data: GroupedLeadConversion[];
  loading: boolean;
  onExport: () => void;
  // Quotes modal props
  selectedEmail: string | null;
  emailQuotes: QuoteDetail[];
  quotesLoading: boolean;
  onRowClick: (email: string) => void;
  onCloseQuotes: () => void;
}

export function ConversionsTable({
  data,
  loading,
  onExport,
  selectedEmail,
  emailQuotes,
  quotesLoading,
  onRowClick,
  onCloseQuotes
}: ConversionsTableProps) {
  const [showOnlyConverted, setShowOnlyConverted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Defensive: ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  const filteredData = useMemo(() => {
    let result = safeData;

    // Filter by search term (name, email, phone, any quote number)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((item) =>
        (item.latestLeadName?.toLowerCase().includes(term)) ||
        (item.email?.toLowerCase().includes(term)) ||
        (item.latestLeadPhone?.includes(term)) ||
        (item.latestQuoteNumber?.toLowerCase().includes(term)) ||
        (item.allQuoteNumbers?.toLowerCase().includes(term))
      );
    }

    // Filter by converted status
    if (showOnlyConverted) {
      result = result.filter((item) => item.hasApprovedQuote);
    }

    return result;
  }, [safeData, showOnlyConverted, searchTerm]);

  const stats = useMemo(() => {
    const converted = safeData.filter((item) => item.hasApprovedQuote).length;
    const total = safeData.length;
    const rate = total > 0 ? (converted / total) * 100 : 0;
    return { converted, total, rate };
  }, [safeData]);

  const formatCurrency = (value: number | null) => {
    if (value == null) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-green-600 text-xs">Aprovado</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="text-xs">Rejeitado</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">Pendente</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conversões de Leads</CardTitle>
          <CardDescription>Leads que geraram orçamentos (agrupados por email)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Carregando...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Conversões de Leads</CardTitle>
            <CardDescription>
              {stats.converted} de {stats.total} leads converteram ({stats.rate.toFixed(1)}%)
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar nome, email, tel, código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 w-[240px]"
              />
            </div>
            <Button
              variant={showOnlyConverted ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowOnlyConverted(!showOnlyConverted)}
            >
              {showOnlyConverted ? 'Mostrar Todos' : 'Apenas Convertidos'}
            </Button>
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredData.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              Nenhuma conversão encontrada
            </div>
          ) : (
            <div className="rounded-md border max-h-[500px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Campanha</TableHead>
                    <TableHead>Fonte</TableHead>
                    <TableHead>Data Lead</TableHead>
                    <TableHead>Orçamento</TableHead>
                    <TableHead>Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item, index) => (
                    <TableRow
                      key={`${item.email}-${index}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => onRowClick(item.email)}
                    >
                      <TableCell>
                        {item.hasApprovedQuote ? (
                          <Badge variant="default" className="bg-green-600">
                            <Check className="h-3 w-3 mr-1" />
                            Convertido
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <X className="h-3 w-3 mr-1" />
                            Não convertido
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{item.latestLeadName}</div>
                          <div className="text-xs text-muted-foreground">{item.email}</div>
                          {item.totalLeadSubmissions > 1 && (
                            <Badge variant="outline" className="text-xs mt-1">
                              {item.totalLeadSubmissions} envios
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.latestLeadPhone || '-'}</TableCell>
                      <TableCell className="max-w-[150px] truncate" title={item.utmCampaign || '-'}>
                        {item.utmCampaign || '-'}
                      </TableCell>
                      <TableCell>{item.utmSource || '-'}</TableCell>
                      <TableCell>{formatDate(item.latestLeadCreatedAt)}</TableCell>
                      <TableCell>
                        {item.latestQuoteNumber ? (
                          <div>
                            <div className="font-medium flex items-center gap-1">
                              {item.latestQuoteNumber}
                              {item.totalQuotes > 1 && (
                                <Badge variant="outline" className="text-xs ml-1">
                                  +{item.totalQuotes - 1}
                                </Badge>
                              )}
                            </div>
                            {getStatusBadge(item.latestQuoteStatus)}
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{formatCurrency(item.latestQuoteAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quotes Modal */}
      <Dialog open={selectedEmail !== null} onOpenChange={() => onCloseQuotes()}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Orçamentos - {selectedEmail}
            </DialogTitle>
            <DialogDescription>
              Todos os orçamentos associados a este email
            </DialogDescription>
          </DialogHeader>

          {quotesLoading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : emailQuotes.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              Nenhum orçamento encontrado para este email
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailQuotes.map((quote) => (
                    <TableRow key={quote.quoteId}>
                      <TableCell className="font-medium">{quote.quoteNumber || '-'}</TableCell>
                      <TableCell>
                        <div>
                          <div>{quote.customerName || '-'}</div>
                          <div className="text-xs text-muted-foreground">{quote.customerPhone}</div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(quote.status)}</TableCell>
                      <TableCell>{formatCurrency(quote.totalAmount)}</TableCell>
                      <TableCell>{formatDate(quote.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
